import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Grid, Input, Popconfirm, Select, theme } from 'antd';
import { Rule } from 'antd/es/form';
import { useNavigate } from 'react-router-dom';
import { FC, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { ApplicantUserProfessional, ApplicantUserProfessionalInput, ApplicantUserPrimaryEducationalInformation, Entity } from '../../../../generated';
import { APOLLO_DATE_FORMAT, DATE_FORMAT } from '../../../../constants';
import { Country } from '../../../../types/country';
import { StateSelect } from '../shared/components/state-select';
import { CountryDropdownContainer } from '../../../shared/country-dropdown/country-dropdown-container';
import { useBeforeUnload } from '../shared/components/custom-hooks';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
export interface PrimaryEducationDetailsProps {
  data: ApplicantUserProfessional;
  countryData: Country[];
  onSave: (values: ApplicantUserProfessionalInput) => void;
  entities: Entity[];
}

const formatDate = (date: string) => {
  return dayjs(date).format(DATE_FORMAT).toUpperCase();
};

export const PrimaryEducationDetails: FC<PrimaryEducationDetailsProps> = (props) => {
  const [isFocus, setIsFocus] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();

  const [educationDetails, setEducationDetails] = useState<ApplicantUserPrimaryEducationalInformation[]>(
    props.data.primaryEducationalInformation as ApplicantUserPrimaryEducationalInformation[]
  );

  const [showOtherSection, setShowOtherSection] = useState<boolean[]>(
    props.data.primaryEducationalInformation?.map((educationDetail) => educationDetail?.issuingInstitution === null && educationDetail?.otherIssuingInstitution !== null) ?? []
  );

  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [formError, setFormError] = useState<string>('');
  const navigate = useNavigate();
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [zipCodeRules, setZipCodeRules] = useState<Rule[][]>(props.data.primaryEducationalInformation?.map(() => [{ required: false, message: '' }]) ?? []);
  const [isUpdatingApplicant, setIsUpdatingApplicant] = useState(false);

  const initialValues = {
    citizenshipAtTimeEnteredMedicalInstitution: props.data.citizenshipAtTimeEnteredMedicalInstitution ?? undefined,
    primaryEducationalInformation: props.data.primaryEducationalInformation
  };

  useEffect(() => {
    const initialCountries: string[] =
      form
        .getFieldsValue()
        .primaryEducationalInformation?.map((educationDetail: ApplicantUserPrimaryEducationalInformation) => educationDetail?.otherIssuingInstitution?.country ?? '') || [];

    const initialZipCodeRules = initialCountries.map((country: string) => {
      return country === 'US' || country === 'CA' ? [{ required: true, message: 'Please enter Zip/Postal Code' }] : [{ required: false, message: '' }];
    });

    setSelectedCountries(initialCountries);
    setZipCodeRules(initialZipCodeRules);
  }, [form]);

  useEffect(() => {
    const primaryEducationalInformation = props.data.primaryEducationalInformation;
    if (primaryEducationalInformation?.length === 0) {
      const initialPrimaryEducationDetails = [{}];
      form.setFieldsValue({
        primaryEducationalInformation: initialPrimaryEducationDetails
      });
      setEducationDetails(initialPrimaryEducationDetails.map((educationDetail) => ({ id: '', ...educationDetail })));
      setShowOtherSection([false]);
    }

    showOtherSection.forEach((showOther, index) => {
      if (showOther && primaryEducationalInformation?.[index]?.issuingInstitution?.id !== 'Other') {
        form.setFieldsValue({
          primaryEducationalInformation: form.getFieldsValue().primaryEducationalInformation.map((educationDetail: ApplicantUserPrimaryEducationalInformation, i: number) => {
            if (i === index) {
              return {
                ...educationDetail,
                issuingInstitution: {
                  id: 'Other',
                  entityName: 'Other'
                }
              };
            }
            return educationDetail;
          })
        });
      }
    });
  }, [props.data]);

  const handleCountryChange = (value: string, index: number) => {
    setSelectedCountries((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });

    // Reset state / province when country changes
    form.setFieldsValue({
      primaryEducationalInformation: form.getFieldsValue().primaryEducationalInformation.map((educationDetail: ApplicantUserPrimaryEducationalInformation, i: number) => {
        if (i === index) {
          return {
            ...educationDetail,
            otherIssuingInstitution: {
              ...educationDetail.otherIssuingInstitution,
              stateOrProvince: ''
            }
          };
        }
        return educationDetail;
      })
    });

    // Update zip code rules for the specific field
    setZipCodeRules((prev) => {
      const updatedRules = [...prev];
      if (value === 'US' || value === 'CA') {
        updatedRules[index] = [{ required: true, message: 'Please enter Zip/Postal Code' }];
      } else {
        updatedRules[index] = [{ required: false, message: '' }];
      }
      return updatedRules;
    });
  };

  const formatApolloDate = (date: dayjs.Dayjs | string | undefined): string | undefined => {
    if (!date) return undefined;
    return dayjs(date).utc().format(APOLLO_DATE_FORMAT);
  };

  const clearValidationErrors = () => {
    const fields = form.getFieldsError().map((field) => ({
      name: field.name,
      errors: []
    }));
    form.setFields(fields); // Clears validation errors but keeps current values
  };

  const onSave = async () => {
    clearValidationErrors();
    setIsFocus(false);
    const values = form.getFieldsValue();
    const sanitizedSubmitValue = values.primaryEducationalInformation.map((educationDetail: ApplicantUserPrimaryEducationalInformation) => {
      const isOther = educationDetail.issuingInstitution?.id === 'Other';
      return {
        id: educationDetail.id ?? undefined,
        attendanceStartedAt: educationDetail?.attendanceStartedAt ? formatApolloDate(educationDetail.attendanceStartedAt) : undefined,
        attendanceEndedAt: educationDetail?.attendanceEndedAt ? formatApolloDate(educationDetail.attendanceEndedAt) : undefined,
        degreeIssuedAt: educationDetail.degreeIssuedAt ? formatApolloDate(educationDetail.degreeIssuedAt) : undefined,
        graduationYear: educationDetail?.graduationYear
          ? (() => {
              if (typeof educationDetail.graduationYear === 'number') {
                return educationDetail.graduationYear;
              } else {
                return parseInt(dayjs(educationDetail.graduationYear).utc().format('YYYY'));
              }
            })()
          : undefined,
        issuingInstitutionId: isOther ? null : educationDetail?.issuingInstitution?.id,
        degreeTitle: educationDetail?.degreeTitle ?? undefined,
        otherIssuingInstitution: isOther ? educationDetail.otherIssuingInstitution : null
      };
    });
    await props.onSave({
      citizenshipAtTimeEnteredMedicalInstitution: values.citizenshipAtTimeEnteredMedicalInstitution,
      primaryEducationalInformation: [...sanitizedSubmitValue]
    });
  };

  const onSubmit = () => {
    setIsFocus(false);
    form
      .validateFields()
      .then((values) => {
        form.submit();
        const sanitizedSubmitValue = values.primaryEducationalInformation.map((educationDetail: ApplicantUserPrimaryEducationalInformation) => {
          const isOther = educationDetail.issuingInstitution?.id === 'Other';
          return {
            id: educationDetail.id,
            attendanceStartedAt: formatApolloDate(educationDetail.attendanceStartedAt),
            attendanceEndedAt: formatApolloDate(educationDetail.attendanceEndedAt),
            degreeIssuedAt: formatApolloDate(educationDetail.degreeIssuedAt),
            graduationYear:
              typeof educationDetail.graduationYear === 'number' ? educationDetail.graduationYear : parseInt(dayjs(educationDetail.graduationYear).utc().format('YYYY')),
            issuingInstitutionId: isOther ? null : educationDetail.issuingInstitution?.id,
            degreeTitle: educationDetail.degreeTitle,
            otherIssuingInstitution: isOther ? educationDetail.otherIssuingInstitution : null
          };
        });
        props.onSave({
          citizenshipAtTimeEnteredMedicalInstitution: values.citizenshipAtTimeEnteredMedicalInstitution,
          primaryEducationalInformation: [...sanitizedSubmitValue]
        });
        navigate(`/applicant/onboarding/passport-details`);
      })
      .catch((error) => {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstErrorField = error.errorFields[0].name;
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
        setIsUpdatingApplicant(false);
        console.log('Form Error', error);
        if (educationDetails.length < 1) {
          setFormError('Please add at least one primary educational information');
        }
      });
  };

  const onEducationDetailsFieldChange = (changedValues: unknown, key: string, index: number) => {
    const updatedEducationDetails = educationDetails.map((educationDetail, i) => {
      if (i === index) {
        if (key === 'issuingInstitution') {
          setShowOtherSection((prev) => {
            const updated = [...prev];
            updated[index] = changedValues === 'Other';
            return updated;
          });
        }
        return {
          ...educationDetail,
          [key]: changedValues
        };
      }
      return educationDetail;
    });
    setEducationDetails(updatedEducationDetails);
  };

  //block-if-not-saved
  useBeforeUnload(isFocus, 'Changes that you made may not be saved.');

  const formFocusHandler = () => {
    setIsFocus(true);
  };

  return (
    <>
      <div
        style={{
          background: colorBgContainer,
          minWidth: 280,
          width: screens.xs ? '100%' : '70%',
          padding: screens.xs ? '24px' : '48px',
          borderRadius: borderRadiusLG,
          display: 'flex',
          marginTop: '24px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'baseline'
        }}
      >
        <h2>Educational Details</h2>
        <Form
          form={form}
          onFocus={formFocusHandler}
          style={{
            width: screens.xs ? '100%' : '70%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
          layout="vertical"
          initialValues={initialValues}
        >
          <p className="ant-form-item-label">Institution that issued your Primary Professional Credential</p>
          <Form.List
            name={['primaryEducationalInformation']}
            rules={[
              {
                validator: async (_, educationDetails) => {
                  if (!educationDetails || educationDetails.length < 1) {
                    return Promise.reject(new Error('Please add at least one primary educational information'));
                  }
                }
              }
            ]}
          >
            {(fields, { add, remove }) => {
              return (
                <div>
                  {fields.map((field, index) => {
                    return (
                      <div
                        key={field.key}
                        style={{
                          marginBottom: '15px',
                          display: 'flex',
                          flexDirection: screens.xs ? 'column' : 'row',
                          width: '100%'
                        }}
                      >
                        <div
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'top',
                            paddingRight: '10px'
                          }}
                        >
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => {
                              if (educationDetails[index].id) {
                                const educationalDetailIndex = educationDetails.indexOf(educationDetails[index].id);
                                if (educationalDetailIndex > -1) {
                                  educationDetails.splice(index, 1);
                                }
                                setEducationDetails([...educationDetails]);
                              }
                              remove(field.name);
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            paddingTop: '3px',
                            paddingLeft: '10px',
                            borderLeft: '1px solid darkgrey',
                            width: screens.xs ? '100%' : 'auto'
                          }}
                        >
                          <Form.Item name={[index, 'id']} hidden={true}>
                            <Input hidden={true} />
                          </Form.Item>
                          <Form.Item
                            style={{ width: screens.xs ? '100%' : 200 }}
                            name={[index, 'degreeTitle']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter Degree Title'
                              }
                            ]}
                            label="Degree Title"
                          >
                            <Input
                              style={{ width: screens.xs ? '100%' : 200 }}
                              onChange={(e) => onEducationDetailsFieldChange(e.target.value, 'degreeTitle', index)}
                              placeholder="Degree Title"
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', flexDirection: screens.xs ? 'column' : 'row', gap: '10px', width: '100%' }}>
                            <Form.Item
                              name={[index, 'attendanceStartedAt']}
                              valuePropName="value"
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select Attendance Start Date'
                                }
                              ]}
                              label="Attendance Start Date"
                              getValueProps={(value) => ({
                                value: value ? dayjs(value).utc() : ''
                              })}
                            >
                              <DatePicker
                                style={{ width: screens.xs ? '100%' : 200 }}
                                value={
                                  props?.data?.primaryEducationalInformation?.[index]?.attendanceStartedAt
                                    ? formatDate(props?.data?.primaryEducationalInformation?.[index]?.attendanceStartedAt)
                                    : ''
                                }
                                maxDate={dayjs(educationDetails[index]?.attendanceEndedAt)}
                                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                                onChange={(date) => {
                                  onEducationDetailsFieldChange(formatDate(date), 'attendanceStartedAt', index);
                                }}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[index, 'attendanceEndedAt']}
                              valuePropName="value"
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select Attendance End Date'
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, attendanceEndedAt) {
                                    const startDate = getFieldValue(['primaryEducationalInformation', index, 'attendanceStartedAt']);
                                    if (dayjs(startDate).isSame(attendanceEndedAt, 'month')) {
                                      return Promise.reject(new Error('Attendance start and end dates cannot be in the same month and year'));
                                    } else {
                                      return Promise.resolve();
                                    }
                                  }
                                })
                              ]}
                              label="Attendance End Date"
                              getValueProps={(value) => ({
                                value: value ? dayjs(value).utc() : ''
                              })}
                            >
                              <DatePicker
                                style={{ width: screens.xs ? '100%' : 200 }}
                                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                                minDate={dayjs(educationDetails[index]?.attendanceStartedAt).utc()}
                                onChange={(date) => onEducationDetailsFieldChange(formatDate(date), 'attendanceEndedAt', index)}
                                value={
                                  props?.data?.primaryEducationalInformation?.[index]?.attendanceEndedAt
                                    ? formatDate(props?.data?.primaryEducationalInformation?.[index]?.attendanceEndedAt)
                                    : ''
                                }
                              />
                            </Form.Item>
                          </div>
                          <div style={{ display: 'flex', flexDirection: screens.xs ? 'column' : 'row', gap: '10px', width: '100%' }}>
                            <Form.Item
                              name={[index, 'degreeIssuedAt']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select Date Degree Issued'
                                }
                              ]}
                              label="Date Degree Issued"
                              getValueProps={(value) => ({
                                value: value ? dayjs(value).utc() : ''
                              })}
                            >
                              <DatePicker
                                style={{ width: screens.xs ? '100%' : 200 }}
                                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                                minDate={dayjs(educationDetails[index]?.attendanceEndedAt).utc()}
                                maxDate={dayjs(dayjs().utc(), DATE_FORMAT)}
                                onChange={(date) => onEducationDetailsFieldChange(formatDate(date), 'degreeIssuedAt', index)}
                                value={
                                  props?.data?.primaryEducationalInformation?.[index]?.degreeIssuedAt
                                    ? formatDate(props?.data?.primaryEducationalInformation?.[index]?.degreeIssuedAt)
                                    : ''
                                }
                              />
                            </Form.Item>
                            <Form.Item
                              name={[index, 'graduationYear']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select Graduation Year'
                                }
                              ]}
                              label="Graduation Year"
                              getValueProps={(value) => {
                                let formattedValue = null;
                                if (value) {
                                  formattedValue = typeof value === 'number' ? dayjs(value.toString(), 'YYYY') : value;
                                }
                                return { value: formattedValue };
                              }}
                            >
                              <DatePicker
                                style={{ width: screens.xs ? '100%' : 200 }}
                                picker="year"
                                format={'YYYY'}
                                maxDate={dayjs().utc()}
                                onChange={(date) => onEducationDetailsFieldChange(dayjs(date).utc().format('YYYY'), 'graduationYear', index)}
                              />
                            </Form.Item>
                          </div>
                          <Form.Item
                            style={{ width: screens.xs ? '100%' : 200 }}
                            name={[index, 'issuingInstitution', 'id']}
                            rules={[
                              {
                                required: true,
                                message: 'Please select Issuing Institution'
                              }
                            ]}
                            label="Issuing Institution"
                          >
                            <Select
                              style={{ width: screens.xs ? '100%' : 410 }}
                              placeholder="Issuing Institution"
                              onSelect={(issuingInstitution) => {
                                onEducationDetailsFieldChange(issuingInstitution, 'issuingInstitution', index);
                              }}
                              onClear={() => {
                                const initialPrimaryEducationDetails = [{}];
                                form.setFieldsValue({
                                  primaryEducationalInformation: initialPrimaryEducationDetails
                                });
                                setEducationDetails(initialPrimaryEducationDetails.map((educationDetail) => ({ id: '', ...educationDetail })));
                                setShowOtherSection([false]);
                              }}
                            >
                              {props.entities.map((entity) => (
                                <Select.Option key={entity.id} value={entity.id}>
                                  {entity.entityName}
                                </Select.Option>
                              ))}
                              <Select.Option key="Other" value="Other">
                                Other
                              </Select.Option>
                            </Select>
                          </Form.Item>
                          {showOtherSection[index] && (
                            <>
                              <Form.Item
                                name={[index, 'otherIssuingInstitution', 'name']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please enter Institution Name'
                                  }
                                ]}
                                label="Institution Name"
                              >
                                <Input
                                  style={{ width: screens.xs ? '100%' : 410 }}
                                  onChange={(otherIssuingInstitutionName) => onEducationDetailsFieldChange(otherIssuingInstitutionName, 'name', index)}
                                  placeholder="Institution Name"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[index, 'otherIssuingInstitution', 'addressLine1']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please enter Address'
                                  }
                                ]}
                                label="Address"
                              >
                                <Input
                                  style={{ width: screens.xs ? '100%' : 410 }}
                                  onChange={(otherIssuingInstitutionAddress) => onEducationDetailsFieldChange(otherIssuingInstitutionAddress, 'addressLine1', index)}
                                  placeholder="Address"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[index, 'otherIssuingInstitution', 'country']}
                                validateTrigger={['onChange', 'onBlur']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select Country'
                                  }
                                ]}
                                label="Country"
                              >
                                <Select
                                  placeholder="Select a country"
                                  showSearch
                                  onChange={(otherIssuingInstitutionCountry) => {
                                    onEducationDetailsFieldChange(otherIssuingInstitutionCountry, 'country', index);
                                    handleCountryChange(otherIssuingInstitutionCountry, index);
                                  }}
                                  optionFilterProp="children"
                                  style={{ width: screens.xs ? '100%' : 410 }}
                                >
                                  {props.countryData.map((country) => (
                                    <Select.Option key={country.countryCode} value={country.countryCode}>
                                      {country.countryName}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: screens.xs ? 'column' : 'row',
                                  gap: '10px',
                                  width: '100%'
                                }}
                              >
                                <Form.Item
                                  style={{ width: screens.xs ? '100%' : 150 }}
                                  label="City"
                                  name={[index, 'otherIssuingInstitution', 'city']}
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please enter City'
                                    }
                                  ]}
                                  validateTrigger={['onChange', 'onBlur']}
                                >
                                  <Input
                                    onChange={(otherIssuingInstitutionCity) => onEducationDetailsFieldChange(otherIssuingInstitutionCity, 'city', index)}
                                    placeholder="City"
                                  />
                                </Form.Item>
                                <StateSelect
                                  name={[index, 'otherIssuingInstitution', 'stateOrProvince']}
                                  selectedCountry={selectedCountries[index]} // Pass the corresponding country
                                  countryData={props.countryData}
                                />
                                <Form.Item
                                  label="Zip/Postal Code"
                                  name={[index, 'otherIssuingInstitution', 'zipOrPostalCode']}
                                  rules={zipCodeRules[index]}
                                  validateTrigger={['onChange', 'onBlur']}
                                >
                                  <Input
                                    style={{ width: screens.xs ? '100%' : 100 }}
                                    maxLength={10}
                                    placeholder="Zip/Postal Code"
                                    onChange={(otherIssuingInstitutionZip) => onEducationDetailsFieldChange(otherIssuingInstitutionZip, 'zipOrPostalCode', index)}
                                  />
                                </Form.Item>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {fields.length < 5 ? (
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => {
                          setEducationDetails([
                            ...educationDetails,
                            {
                              id: '',
                              degreeTitle: '',
                              attendanceStartedAt: '',
                              attendanceEndedAt: '',
                              degreeIssuedAt: '',
                              issuingInstitution: {
                                id: '',
                                entityName: ''
                              },
                              otherIssuingInstitution: {
                                name: '',
                                addressLine1: '',
                                city: '',
                                stateOrProvince: '',
                                zipOrPostalCode: '',
                                country: ''
                              }
                            }
                          ]);
                          setShowOtherSection((prev) => [...prev, false]);
                          add();
                        }}
                        block
                      >
                        <PlusOutlined /> Other Educational Institution
                      </Button>
                    </Form.Item>
                  ) : (
                    <></>
                  )}
                </div>
              );
            }}
          </Form.List>
          <CountryDropdownContainer
            name={['citizenshipAtTimeEnteredMedicalInstitution']}
            rules={[
              {
                required: true,
                message: 'Please select Citizenship Upon Entering Educational Institution'
              }
            ]}
            label="Country of Citizenship at the Time You Entered Institution"
            style={{ width: screens.xs ? '100%' : 450 }}
            placeholder="Select Country"
          />
          {formError && educationDetails.length < 1 && <p style={{ color: '#ff4d4f' }}>{formError}</p>}
        </Form>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '24px 0px',
          gap: screens.xs ? '10px' : '0',
          width: screens.xs ? '100%' : '70%'
        }}
      >
        <div>
          <Popconfirm
            title={isFocus ? 'Are you sure? Changes you made will not be saved.' : null}
            onConfirm={() => navigate(`/applicant/onboarding/contact-details`)}
            disabled={!isFocus}
          >
            <Button
              onClick={() => (isFocus ? null : navigate(`/applicant/onboarding/contact-details`))}
              style={{ width: screens.xs ? '80px' : '90%', height: screens.xs ? '50px' : 'auto' }}
            >
              {'<< Back'}
            </Button>
          </Popconfirm>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: screens.xs ? 'column' : 'row',
            gap: '15px'
          }}
        >
          <Popconfirm title="Are you sure? Your entries on this page will be deleted." onConfirm={() => navigate('/applicant/onboarding')} disabled={isUpdatingApplicant}>
            <Button danger style={{ height: screens.xs ? '50px' : 'auto' }}>
              Withdraw Request
            </Button>
          </Popconfirm>
          <Button onClick={onSave} style={{ height: screens.xs ? '50px' : 'auto' }} loading={isUpdatingApplicant}>
            Save Progress
          </Button>
          <Button type="primary" onClick={() => onSubmit()} style={{ height: screens.xs ? '50px' : 'auto' }} disabled={isUpdatingApplicant}>
            {'Save and Continue >>'}
          </Button>
        </div>
      </div>
    </>
  );
};
